import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { useEvent, useEventUsers, useTransferTokens, useUserTransactionMetrics } from '@/lib/api/queries'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Button } from '@/components/ui/button'
import { Search, User, ArrowUpCircle, ArrowDownCircle, RefreshCw, Check, X, ArrowLeft, Eye } from 'lucide-react'
import { useState, useRef, useEffect } from 'react'
import { usePermission } from '@/lib/permissions'
import { AccessDenied } from '@/components/permission-guard'
import { useModal } from '@/components/modals/modal-container'
import { useDebounce } from '@/lib/hooks/use-debounce'
import { cn } from '@/lib/utils'
import type { User as UserType } from '@/lib/api/types'

export const Route = createFileRoute('/_authenticated/events/$eventId/transfer')({
  component: TransferPage,
  validateSearch: (search: Record<string, unknown>) => {
    return {
      fromUserId: search.fromUserId ? Number(search.fromUserId) : undefined,
      toUserId: search.toUserId ? Number(search.toUserId) : undefined,
    }
  },
})

function TransferPage() {
  const { eventId } = Route.useParams()
  const { fromUserId: preselectedFromUserId, toUserId: preselectedToUserId } = Route.useSearch()
  const eventIdNum = Number(eventId)
  const navigate = useNavigate()
  const { openUserModal } = useModal()
  
  // Permission checks - must be before any early returns
  const canTransfer = usePermission('can_transfer')
  
  // Search states
  const [fromSearch, setFromSearch] = useState('')
  const [toSearch, setToSearch] = useState('')
  const debouncedFromSearch = useDebounce(fromSearch, 300)
  const debouncedToSearch = useDebounce(toSearch, 300)
  
  // Selected users
  const [fromUser, setFromUser] = useState<UserType | null>(null)
  const [toUser, setToUser] = useState<UserType | null>(null)
  
  // Transfer state
  const [transferAmount, setTransferAmount] = useState<number>(1)
  const [success, setSuccess] = useState(false)
  const [error, setError] = useState<string | null>(null)
  
  const fromSearchRef = useRef<HTMLInputElement>(null)
  
  // All hooks must be called before conditional returns
  const { data: event } = useEvent(eventIdNum)
  
  // Search queries
  const { data: fromUsersData, isLoading: fromLoading } = useEventUsers(eventIdNum, {
    search: debouncedFromSearch || undefined,
    page_size: 10,
  })
  const { data: toUsersData, isLoading: toLoading } = useEventUsers(eventIdNum, {
    search: debouncedToSearch || undefined,
    page_size: 10,
  })
  
  // Preselected user queries
  const { data: preselectedFromData } = useEventUsers(eventIdNum, {
    search: preselectedFromUserId ? String(preselectedFromUserId) : undefined,
    page_size: 1,
  })
  const { data: preselectedToData } = useEventUsers(eventIdNum, {
    search: preselectedToUserId ? String(preselectedToUserId) : undefined,
    page_size: 1,
  })
  
  // Get accurate balances using transaction metrics
  const tokenValue = event?.token_value || 2
  const { data: fromUserMetrics } = useUserTransactionMetrics(
    fromUser?.id || 0,
    eventIdNum,
    tokenValue
  )
  const { data: toUserMetrics } = useUserTransactionMetrics(
    toUser?.id || 0,
    eventIdNum,
    tokenValue
  )
  
  const transferTokens = useTransferTokens()
  
  const fromUsers = fromUsersData?.results || []
  const toUsers = toUsersData?.results || []
  
  // Set preselected users on mount
  useEffect(() => {
    if (preselectedFromUserId && preselectedFromData?.results?.length) {
      const user = preselectedFromData.results.find(u => u.id === preselectedFromUserId)
      if (user) setFromUser(user)
    }
  }, [preselectedFromUserId, preselectedFromData])
  
  useEffect(() => {
    if (preselectedToUserId && preselectedToData?.results?.length) {
      const user = preselectedToData.results.find(u => u.id === preselectedToUserId)
      if (user) setToUser(user)
    }
  }, [preselectedToUserId, preselectedToData])
  
  // Focus search on mount
  useEffect(() => {
    if (!fromUser && fromSearchRef.current) {
      fromSearchRef.current.focus()
    }
  }, [fromUser])
  
  // Permission guard - now safe to return early since all hooks have been called
  if (!canTransfer) {
    return <AccessDenied message="You don't have permission to transfer tokens." />
  }
  
  const fromBalance = fromUserMetrics?.balance_tokens ?? 0
  const toBalance = toUserMetrics?.balance_tokens ?? 0
  
  const canProcessTransfer = () => {
    if (!fromUser || !toUser) return false
    if (fromUser.id === toUser.id) return false
    if (transferAmount <= 0) return false
    if (transferAmount > fromBalance) return false
    return true
  }
  
  const switchUsers = () => {
    const temp = fromUser
    setFromUser(toUser)
    setToUser(temp)
  }
  
  const handleTransfer = async () => {
    if (!canProcessTransfer() || !fromUser || !toUser) return
    
    setError(null)
    
    try {
      await transferTokens.mutateAsync({
        eventId: eventIdNum,
        fromUserId: fromUser.id,
        toUserId: toUser.id,
        amount: transferAmount,
      })
      setSuccess(true)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Transfer failed. Please try again.')
    }
  }
  
  const resetForm = () => {
    setFromUser(null)
    setToUser(null)
    setTransferAmount(1)
    setSuccess(false)
    setError(null)
    setFromSearch('')
    setToSearch('')
  }
  
  // Success state
  if (success) {
    return (
      <div className="p-6 max-w-2xl mx-auto">
        <Card>
          <CardContent className="pt-6">
            <div className="text-center">
              <div className="w-16 h-16 bg-green-100 dark:bg-green-900/30 rounded-full flex items-center justify-center mx-auto mb-4">
                <Check className="w-8 h-8 text-green-600 dark:text-green-400" />
              </div>
              <h3 className="text-xl font-semibold text-neutral-900 dark:text-white mb-2">
                Transfer Completed Successfully!
              </h3>
              <p className="text-neutral-600 dark:text-neutral-400 mb-6">
                {transferAmount} tokens have been transferred from {fromUser?.first_name} {fromUser?.last_name} to {toUser?.first_name} {toUser?.last_name}
              </p>
              
              {/* Transfer Details */}
              <div className="bg-neutral-50 dark:bg-neutral-900 border border-neutral-200 dark:border-neutral-800 rounded-lg p-4 mb-6 text-left">
                <h4 className="font-semibold text-neutral-900 dark:text-white mb-3">Transfer Details</h4>
                <div className="space-y-3 text-sm">
                  <div className="flex justify-between">
                    <span className="text-neutral-600 dark:text-neutral-400">Transfer Amount:</span>
                    <span className="text-neutral-900 dark:text-white font-semibold">{transferAmount} Tokens</span>
                  </div>
                  
                  <div className="border-t border-neutral-200 dark:border-neutral-700 pt-3 mt-3">
                    <div className="flex items-center gap-2 mb-2">
                      <ArrowUpCircle className="w-4 h-4 text-blue-500" />
                      <span className="font-medium text-neutral-900 dark:text-white">From</span>
                    </div>
                    <div className="pl-6 space-y-1">
                      <div className="flex justify-between">
                        <span className="text-neutral-600 dark:text-neutral-400">Name:</span>
                        <span className="text-neutral-900 dark:text-white">{fromUser?.first_name} {fromUser?.last_name}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-neutral-600 dark:text-neutral-400">Email:</span>
                        <span className="text-neutral-900 dark:text-white">{fromUser?.email}</span>
                      </div>
                    </div>
                  </div>
                  
                  <div className="border-t border-neutral-200 dark:border-neutral-700 pt-3">
                    <div className="flex items-center gap-2 mb-2">
                      <ArrowDownCircle className="w-4 h-4 text-green-500" />
                      <span className="font-medium text-neutral-900 dark:text-white">To</span>
                    </div>
                    <div className="pl-6 space-y-1">
                      <div className="flex justify-between">
                        <span className="text-neutral-600 dark:text-neutral-400">Name:</span>
                        <span className="text-neutral-900 dark:text-white">{toUser?.first_name} {toUser?.last_name}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-neutral-600 dark:text-neutral-400">Email:</span>
                        <span className="text-neutral-900 dark:text-white">{toUser?.email}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              
              <div className="flex flex-col sm:flex-row gap-3 justify-center">
                <Button onClick={resetForm}>
                  Transfer More Tokens
                </Button>
                <Button 
                  variant="outline"
                  onClick={() => navigate({ to: '/events/$eventId/support', params: { eventId } })}
                >
                  Back to Support
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    )
  }

  return (
    <div className="p-6 max-w-6xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-2xl font-bold text-neutral-900 dark:text-white">Transfer Tokens</h1>
          <p className="text-neutral-600 dark:text-neutral-400 mt-1">
            {event?.name ? `${event.name} ` : ''}
          </p>
        </div>
        <Button 
          variant="outline"
          onClick={() => navigate({ to: '/events/$eventId/support', params: { eventId } })}
          className="gap-2"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to Support
        </Button>
      </div>

      {/* Error Alert */}
      {error && (
        <div className="mb-6 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg flex items-center gap-3">
          <X className="w-5 h-5 text-red-600 dark:text-red-400" />
          <p className="text-red-700 dark:text-red-300">{error}</p>
        </div>
      )}

      {/* Transfer Interface */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        
        {/* From User (Left) */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg font-semibold text-neutral-900 dark:text-white flex items-center gap-2">
              <ArrowUpCircle className="w-5 h-5 text-blue-500" />
              From User
            </CardTitle>
          </CardHeader>
          <CardContent>
            {fromUser ? (
              <div className="p-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <h3 className="font-medium text-neutral-900 dark:text-white">
                      {fromUser.first_name} {fromUser.last_name}
                    </h3>
                    <p className="text-sm text-neutral-600 dark:text-neutral-400">{fromUser.email}</p>
                    {fromUser.phone_number && (
                      <p className="text-sm text-neutral-600 dark:text-neutral-400">{fromUser.phone_number}</p>
                    )}
                    <div className="flex items-center gap-2 mt-2">
                      <span className="text-sm font-medium text-neutral-900 dark:text-white">Balance:</span>
                      <span className="text-sm text-neutral-900 dark:text-white">{fromBalance} tokens</span>
                    </div>
                  </div>
                  <div className="flex flex-col gap-2">
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => openUserModal(fromUser.id, eventIdNum)}
                      className="text-blue-600 hover:text-blue-700 dark:text-blue-400"
                    >
                      <Eye className="w-4 h-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => setFromUser(null)}
                      className="text-blue-600 hover:text-blue-700 dark:text-blue-400"
                    >
                      <X className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </div>
            ) : (
              <>
                <div className="relative mb-4">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-neutral-500" />
                  <Input
                    ref={fromSearchRef}
                    type="text"
                    placeholder="Search users to transfer from..."
                    value={fromSearch}
                    onChange={(e) => setFromSearch(e.target.value)}
                    className="pl-10"
                  />
                </div>
                
                <div className="space-y-2 max-h-80 overflow-y-auto">
                  {fromLoading || (fromSearch && fromSearch !== debouncedFromSearch) ? (
                    <div className="py-8 text-center">
                      <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-500 mx-auto" />
                    </div>
                  ) : debouncedFromSearch && fromUsers.length === 0 ? (
                    <div className="py-8 text-center text-neutral-500 dark:text-neutral-400 text-sm">
                      No users found
                    </div>
                  ) : debouncedFromSearch && fromUsers.length > 0 ? (
                    fromUsers.map((user) => (
                      <button
                        key={user.id}
                        onClick={() => {
                          setFromUser(user)
                          setFromSearch('')
                        }}
                        disabled={user.id === toUser?.id}
                        className={cn(
                          "w-full flex items-center gap-3 p-3 rounded-lg border text-left transition-colors",
                          user.id === toUser?.id
                            ? "opacity-50 cursor-not-allowed border-neutral-200 dark:border-neutral-800"
                            : "border-neutral-200 dark:border-neutral-800 hover:bg-neutral-50 dark:hover:bg-neutral-900/50"
                        )}
                      >
                        <div className="w-8 h-8 rounded-full bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center">
                          <User className="w-4 h-4 text-blue-600 dark:text-blue-400" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-neutral-900 dark:text-white text-sm truncate">
                            {user.first_name} {user.last_name}
                          </p>
                          <p className="text-xs text-neutral-500 dark:text-neutral-400 truncate">
                            {user.email}
                          </p>
                        </div>
                      </button>
                    ))
                  ) : (
                    <div className="py-8 text-center text-neutral-500 dark:text-neutral-400 text-sm">
                      Search for a user
                    </div>
                  )}
                </div>
              </>
            )}
          </CardContent>
        </Card>

        {/* Transfer Amount (Center) */}
        <Card className="flex flex-col">
          <CardContent className="flex-1 flex flex-col items-center justify-center pt-6">
            {/* Switch Button */}
            <Button
              variant="outline"
              size="icon"
              onClick={switchUsers}
              disabled={!fromUser && !toUser}
              className="mb-6 rounded-full w-12 h-12"
              title="Switch users"
            >
              <RefreshCw className="w-5 h-5" />
            </Button>

            {/* Amount Input */}
            <div className="w-full max-w-xs">
              <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-2 text-center">
                Transfer Amount
              </label>
              <div className="relative">
                <Input
                  type="number"
                  value={transferAmount}
                  onChange={(e) => setTransferAmount(Math.max(1, parseInt(e.target.value) || 1))}
                  min={1}
                  max={fromBalance || 999999}
                  className={cn(
                    "text-center text-xl font-semibold pr-16",
                    transferAmount > fromBalance && fromUser ? "border-red-500 focus-visible:ring-red-500" : ""
                  )}
                />
                <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                  <span className="text-neutral-500 dark:text-neutral-400 font-medium text-sm">tokens</span>
                </div>
              </div>
              {transferAmount > fromBalance && fromUser && (
                <p className="text-red-500 text-xs mt-1 text-center">
                  Insufficient balance
                </p>
              )}
              {fromUser && toUser && fromUser.id === toUser.id && (
                <p className="text-red-500 text-xs mt-1 text-center">
                  Cannot transfer to the same user
                </p>
              )}
            </div>

            {/* Transfer Button */}
            <Button
              onClick={handleTransfer}
              disabled={!canProcessTransfer() || transferTokens.isPending}
              className="mt-6 px-8"
              size="lg"
            >
              {transferTokens.isPending ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2" />
                  Processing...
                </>
              ) : (
                'Transfer Tokens'
              )}
            </Button>
          </CardContent>
        </Card>

        {/* To User (Right) */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg font-semibold text-neutral-900 dark:text-white flex items-center gap-2">
              <ArrowDownCircle className="w-5 h-5 text-green-500" />
              To User
            </CardTitle>
          </CardHeader>
          <CardContent>
            {toUser ? (
              <div className="p-4 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg">
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <h3 className="font-medium text-neutral-900 dark:text-white">
                      {toUser.first_name} {toUser.last_name}
                    </h3>
                    <p className="text-sm text-neutral-600 dark:text-neutral-400">{toUser.email}</p>
                    {toUser.phone_number && (
                      <p className="text-sm text-neutral-600 dark:text-neutral-400">{toUser.phone_number}</p>
                    )}
                    <div className="flex items-center gap-2 mt-2">
                      <span className="text-sm font-medium text-neutral-900 dark:text-white">Balance:</span>
                      <span className="text-sm text-neutral-900 dark:text-white">{toBalance} tokens</span>
                    </div>
                  </div>
                  <div className="flex flex-col gap-2">
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => openUserModal(toUser.id, eventIdNum)}
                      className="text-green-600 hover:text-green-700 dark:text-green-400"
                    >
                      <Eye className="w-4 h-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => setToUser(null)}
                      className="text-green-600 hover:text-green-700 dark:text-green-400"
                    >
                      <X className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </div>
            ) : (
              <>
                <div className="relative mb-4">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-neutral-500" />
                  <Input
                    type="text"
                    placeholder="Search users to transfer to..."
                    value={toSearch}
                    onChange={(e) => setToSearch(e.target.value)}
                    className="pl-10"
                  />
                </div>
                
                <div className="space-y-2 max-h-80 overflow-y-auto">
                  {toLoading || (toSearch && toSearch !== debouncedToSearch) ? (
                    <div className="py-8 text-center">
                      <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-green-500 mx-auto" />
                    </div>
                  ) : debouncedToSearch && toUsers.length === 0 ? (
                    <div className="py-8 text-center text-neutral-500 dark:text-neutral-400 text-sm">
                      No users found
                    </div>
                  ) : debouncedToSearch && toUsers.length > 0 ? (
                    toUsers.map((user) => (
                      <button
                        key={user.id}
                        onClick={() => {
                          setToUser(user)
                          setToSearch('')
                        }}
                        disabled={user.id === fromUser?.id}
                        className={cn(
                          "w-full flex items-center gap-3 p-3 rounded-lg border text-left transition-colors",
                          user.id === fromUser?.id
                            ? "opacity-50 cursor-not-allowed border-neutral-200 dark:border-neutral-800"
                            : "border-neutral-200 dark:border-neutral-800 hover:bg-neutral-50 dark:hover:bg-neutral-900/50"
                        )}
                      >
                        <div className="w-8 h-8 rounded-full bg-green-100 dark:bg-green-900/30 flex items-center justify-center">
                          <User className="w-4 h-4 text-green-600 dark:text-green-400" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-neutral-900 dark:text-white text-sm truncate">
                            {user.first_name} {user.last_name}
                          </p>
                          <p className="text-xs text-neutral-500 dark:text-neutral-400 truncate">
                            {user.email}
                          </p>
                        </div>
                      </button>
                    ))
                  ) : (
                    <div className="py-8 text-center text-neutral-500 dark:text-neutral-400 text-sm">
                      Search for a user
                    </div>
                  )}
                </div>
              </>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  )
}
